import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ARTICLES } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";

export const WritingsPreview = () => {
  const [lead, second, third, ...rest] = ARTICLES;
  const sidebar = rest.slice(0, 5);

  return (
    <section
      id="writings"
      data-testid="writings-feed-section"
      className="relative py-24 lg:py-36 bg-[var(--surface)] border-y border-[var(--border-c)] scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        {/* Masthead */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-8 border-b-2 border-[var(--text)]">
          <SectionHeading
            eyebrow="Devata.org · The Journal"
            title={
              <>
                Field notes from the
                <br />
                sacred archive
              </>
            }
          />
          <Reveal delay={0.1}>
            <div className="flex items-end gap-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-2)]">
                  Issue No.
                </div>
                <div className="font-serif text-4xl text-[var(--text)] mt-1 tabular-nums">
                  2026 · I
                </div>
              </div>
              <Link
                to="/writings"
                data-testid="view-all-writings-link"
                className="group inline-flex items-center gap-2 rounded-full border border-[var(--text)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text)] hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors"
              >
                All {ARTICLES.length} Writings
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>

        {/* Hero + sidebar grid */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEAD */}
          <Reveal className="lg:col-span-7">
            <Link
              to={`/writings/${lead.id}`}
              data-testid="article-card-item"
              className="group block"
            >
              <div className="relative overflow-hidden rounded-sm aspect-[16/11] bg-[var(--surface-2)]">
                <img
                  src={lead.image}
                  alt={lead.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-[var(--gold)]/20" />
              </div>
              <div className="mt-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.24em]">
                <span className="text-[var(--terracotta)]">{lead.category}</span>
                <span className="h-px w-6 bg-[var(--border-c)]" />
                <span className="text-[var(--text-2)]">{lead.date}</span>
              </div>
              <h3 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-[var(--text)] group-hover:text-[var(--terracotta)] transition-colors duration-500 max-w-2xl">
                {lead.title}
              </h3>
              <p className="mt-4 font-sans text-base leading-relaxed text-[var(--text-2)] max-w-xl">
                {lead.excerpt}
              </p>
              <div className="mt-5 flex items-center gap-4">
                <span className="font-sans text-sm text-[var(--text)]">
                  By {lead.author}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">
                  {lead.readTime}
                </span>
              </div>
            </Link>
          </Reveal>

          {/* SIDEBAR LIST */}
          <div className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="flex items-baseline justify-between pb-4 border-b border-[var(--border-c)]">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-2)]">
                  More from the Journal
                </span>
                <span className="font-serif italic text-sm text-[var(--text-2)]">
                  continued →
                </span>
              </div>
            </Reveal>
            <ul className="divide-y divide-[var(--border-c)]">
              {[second, third, ...sidebar].slice(0, 5).map((a, i) => (
                <Reveal key={a.id} delay={0.05 + i * 0.04}>
                  <li>
                    <Link
                      to={`/writings/${a.id}`}
                      data-testid="article-card-item"
                      className="group grid grid-cols-[auto_1fr_auto] items-start gap-5 py-5"
                    >
                      <span className="font-serif text-2xl text-[var(--gold)]/70 tabular-nums pt-1">
                        {String(i + 2).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--terracotta)]">
                          {a.category}
                        </span>
                        <h4 className="mt-1 font-serif text-xl leading-snug text-[var(--text)] group-hover:text-[var(--terracotta)] transition-colors duration-300">
                          {a.title}
                        </h4>
                        <span className="mt-1.5 block font-sans text-xs text-[var(--text-2)]">
                          {a.date} · {a.readTime}
                        </span>
                      </div>
                      <motion.span
                        className="h-10 w-10 rounded-full border border-[var(--border-c)] grid place-items-center text-[var(--text-2)] group-hover:bg-[var(--text)] group-hover:text-[var(--bg)] group-hover:border-[var(--text)] transition-colors"
                        whileHover={{ rotate: -8 }}
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </motion.span>
                    </Link>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
